import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { EASE, Reveal } from "@/components/Reveal";
import { INDUSTRIES } from "@/data/content";

const FILTERS = ["All", "B2B", "B2C", "D2C", "Enterprise"];

export default function Industries() {
  const [filter, setFilter] = useState("All");
  const visible = INDUSTRIES.filter((i) => filter === "All" || i.tags.includes(filter));

  return (
    <div data-testid="industries-page">
      <Seo
        title="Industries — Vigarbiz"
        siteName="Vigarbiz"
        description="Growth playbooks tuned per vertical: SaaS, e-commerce, healthcare, fintech, real estate, education, hospitality and manufacturing."
      />
      <PageHeader
        overline="Industries"
        lines={["Vertical fluency,", <span key="n" className="text-brand">Not generic</span>, "Playbooks."]}
        description="A funnel in fintech behaves nothing like a funnel in fashion. We bring vertical-specific benchmarks, compliance instincts and creative that speaks the customer's language."
      />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap gap-2" data-testid="industry-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`industry-filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`rounded-full border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  filter === f
                    ? "border-brand bg-brand text-ink"
                    : "border-white/15 text-muted2 hover:border-brand/50 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((ind) => (
              <motion.div
                layout
                key={ind.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: EASE }}
                data-testid={`industry-card-${ind.id}`}
                className="glow-card rounded-2xl border border-white/10 bg-surface p-6 sm:p-8"
              >
                <ind.icon className="h-8 w-8 text-brand" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-xl font-bold text-white sm:text-2xl">{ind.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted2">{ind.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {ind.tags.map((t) => (
                    <span key={t} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-dim">
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <CtaBand title="Your vertical. Our velocity." sub="Tell us your market and we'll show you the benchmarks we've already moved in it." />
    </div>
  );
}
